import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiMenuAlt3, HiX } from 'react-icons/hi';

const HeaderV2 = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'About', href: '#about' },
    { name: 'Services', href: '#services' },
    { name: 'Methodology', href: '#methodology' },
    { name: 'Insights', href: '#insights' },
    { name: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      data-testid="header-v2"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/90 backdrop-blur-xl shadow-sm py-4' : 'bg-transparent py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#"
          data-testid="header-logo-v2"
          className="text-2xl font-semibold tracking-wide bg-gradient-to-r from-[#5B8FB9] via-[#7CB98F] to-[#D4A5B5] bg-clip-text text-transparent"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          SUSANA
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav-v2">
          {navLinks.map((link) => (
            <button
              key={link.name}
              onClick={() => scrollToSection(link.href)}
              data-testid={`nav-link-v2-${link.name.toLowerCase()}`}
              className="text-sm font-medium text-gray-600 hover:text-[#5B8FB9] transition-colors relative after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 after:bg-gradient-to-r after:from-[#5B8FB9] after:to-[#7CB98F] after:transition-all after:duration-300 hover:after:w-full"
            >
              {link.name}
            </button>
          ))}
        </nav>

        {/* Desktop CTA */}
        <a
          href="#contact"
          data-testid="header-cta-v2"
          className="hidden md:inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-[#5B8FB9] to-[#7CB98F] text-white font-medium rounded-full transition-all duration-300 hover:shadow-lg hover:shadow-[#5B8FB9]/30 hover:scale-105"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection('#contact');
          }}
        >
          Book a Call
        </a>

        {/* Mobile Menu Toggle */}
        <button
          data-testid="mobile-menu-toggle-v2"
          className="md:hidden text-[#5B8FB9] p-2"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <HiX size={24} /> : <HiMenuAlt3 size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-gray-100"
            data-testid="mobile-menu-v2"
          >
            <nav className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => scrollToSection(link.href)}
                  className="text-lg font-medium text-gray-600 hover:text-[#5B8FB9] transition-colors text-left"
                >
                  {link.name}
                </button>
              ))}
              <button
                onClick={() => scrollToSection('#contact')}
                className="mt-4 px-6 py-3 bg-gradient-to-r from-[#5B8FB9] to-[#7CB98F] text-white font-medium rounded-full text-center"
              >
                Book a Call
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default HeaderV2;
